/**
 * DiscoverPage — „Entdecken"-Feed (Jun-2026, User-Wunsch · B2C mit KI).
 *
 * Personalisierter Stream aus mehreren Sektionen (KI-Empfehlungen, Preissenkungen
 * bei Favoriten, bald endende Auktionen, Neuzugänge). Backend: GET /api/discover/feed.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Compass, Sparkles, TrendingDown, Gavel, Clock, Flame, Loader2, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import ListingCard from "../components/ListingCard";

const SECTION_ICON = {
    for_you: Sparkles,
    price_drops: TrendingDown,
    ending_auctions: Gavel,
    new_arrivals: Clock,
    trending: Flame,
};

function FeedSection({ section, favIds, onToggleFav }) {
    const Icon = SECTION_ICON[section.key] || Compass;
    return (
        <section className="mb-10" data-testid={`discover-section-${section.key}`}>
            <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-5 h-5 ${section.ai ? "text-indigo-600" : "text-[#111827]"}`} />
                <h2 className="font-display text-xl font-black text-[#111827]">{section.title}</h2>
                {section.ai && (
                    <span className="text-[10px] uppercase tracking-wider font-bold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-sm" data-testid="discover-ai-badge">KI</span>
                )}
            </div>
            {section.subtitle && <p className="text-sm text-gray-500 mb-4">{section.subtitle}</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {section.items.map((listing) => (
                    <div key={listing.id} className="relative" data-testid={`discover-item-${listing.id}`}>
                        {listing.discover_reason && (
                            <div className="absolute z-10 top-2 left-2 right-2 flex">
                                <span className="inline-flex items-center gap-1 bg-indigo-600/95 text-white text-[11px] font-semibold px-2 py-1 rounded-sm shadow-sm backdrop-blur-sm" data-testid={`discover-reason-${listing.id}`}>
                                    <Sparkles className="w-3 h-3 shrink-0" /> {listing.discover_reason}
                                </span>
                            </div>
                        )}
                        {listing.fav_price_drop && (
                            <div className="absolute z-10 top-2 right-2">
                                <span className="inline-flex items-center gap-1 bg-green-600 text-white text-[11px] font-bold px-2 py-1 rounded-sm shadow-sm" data-testid={`discover-drop-${listing.id}`}>
                                    <TrendingDown className="w-3 h-3" /> −{listing.fav_price_drop.pct}%
                                </span>
                            </div>
                        )}
                        <ListingCard
                            listing={listing}
                            vertical
                            onToggleFavorite={onToggleFav}
                            isFavorited={favIds.includes(listing.id)}
                        />
                    </div>
                ))}
            </div>
        </section>
    );
}

export default function DiscoverPage() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [favIds, setFavIds] = useState([]);

    useEffect(() => {
        let active = true;
        api.get("/discover/feed")
            .then((r) => { if (active) { setData(r.data); setLoading(false); } })
            .catch(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [user]);

    useEffect(() => {
        if (user && user !== false) {
            api.get("/favorites/ids").then((r) => setFavIds(r.data)).catch(() => {});
        }
    }, [user]);

    const toggleFav = async (id) => {
        if (!user || user === false) { window.location.href = "/login"; return; }
        const { data: res } = await api.post(`/favorites/${id}`);
        setFavIds((prev) => (res.favorited ? [...prev, id] : prev.filter((x) => x !== id)));
    };

    const sections = data?.sections || [];

    return (
        <div className="max-w-7xl mx-auto px-4 py-8" data-testid="discover-page">
            <div className="flex items-center gap-3 mb-1">
                <Compass className="w-7 h-7 text-indigo-600" />
                <h1 className="font-display text-3xl sm:text-4xl font-black text-[#111827]">Entdecken</h1>
            </div>
            <p className="text-base text-gray-600 mb-8">
                {data?.personalized
                    ? "Frische Empfehlungen, KI-kuratiert nach deinen Interessen, Favoriten und letzten Ansichten."
                    : "Entdecke beliebte Fahrzeuge, bald endende Auktionen und Neuzugänge. Melde dich an für persönliche KI-Empfehlungen."}
            </p>

            {loading ? (
                <div className="flex items-center justify-center py-24 text-gray-400" data-testid="discover-loading">
                    <Loader2 className="w-6 h-6 animate-spin mr-2" /> Feed wird zusammengestellt…
                </div>
            ) : sections.length === 0 ? (
                <div className="text-center py-24" data-testid="discover-empty">
                    <p className="text-gray-500 mb-4">Aktuell gibt es nichts zu entdecken.</p>
                    <Link to="/search?category=motorcycle" className="inline-flex items-center gap-1.5 text-indigo-600 font-bold hover:underline" data-testid="discover-browse-link">
                        Zur Fahrzeugsuche <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            ) : (
                sections.map((s) => (
                    <FeedSection key={s.key} section={s} favIds={favIds} onToggleFav={toggleFav} />
                ))
            )}
        </div>
    );
}
